import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import LoteUploadModal from "./LoteUploadModal";
import LoteProgressPanel from "./LoteProgressPanel";
import { criarLoteEmplacamento } from "../../../servicos/emplacarLotes";
import { normalizeJobUpdate, useLoteJobSubscription } from "../hooks/useLoteJobSubscription";
import {
  ensureString,
  normalizarNfe,
  normalizarChassi,
  normalizarArquivo,
  associarJobItemsAosEnvios,
} from "./loteUtils";

const SUCCESS_STATUSES = [
  "sucesso",
  "success",
  "concluido",
  "concluído",
  "finalizado",
  "completed",
  "done",
];

const FAILURE_STATUSES = ["erro", "error", "falha", "failed"];
const PROCESSED_EXTRA_STATUSES = ["concluido", "concluído", "ignored", "ignorado"];
const STORAGE_KEY = "envioLoteState";
const PROCESSED_KEY = "envioLoteProcessed";
const INCOMPLETE_REASON = "Arquivo sem dados suficientes (chave NFe e chassi).";
const DUPLICATE_REASON = "Duplicado (já enviado anteriormente).";
const normalizeStatus = (status) => ensureString(status).toLowerCase();
const isSuccessStatus = (statusLower) => SUCCESS_STATUSES.includes(statusLower);
const isFailureStatus = (statusLower) => FAILURE_STATUSES.includes(statusLower);
const isProcessedStatus = (statusLower) =>
  isSuccessStatus(statusLower) || isFailureStatus(statusLower) || PROCESSED_EXTRA_STATUSES.includes(statusLower);

const calcularResumoProcessamento = (resumo, itens) => {
  const total = Number.isFinite(resumo?.total) ? resumo.total : itens.length;

  const fallbackSuccess = itens.filter((item) => isSuccessStatus(normalizeStatus(item.status))).length;
  const fallbackFailed = itens.filter((item) => isFailureStatus(normalizeStatus(item.status))).length;
  const fallbackProcessed = itens.filter((item) => isProcessedStatus(normalizeStatus(item.status))).length;

  const processed = Number.isFinite(resumo?.processed) ? resumo.processed : fallbackProcessed;
  const success = Number.isFinite(resumo?.success) ? resumo.success : fallbackSuccess;
  const failed = Number.isFinite(resumo?.failed) ? resumo.failed : fallbackFailed;
  const percentual = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return { total, processed, success, failed, percentual };
};

const montarChave = (nfe, chassi) => `${ensureString(nfe)}::${ensureString(chassi)}`;

const normalizeProcessoDetranValue = (value) => {
  const trimmed = ensureString(value).trim();
  return trimmed || null;
};

const extractProcessoDetranFromItem = (item = {}) =>
  normalizeProcessoDetranValue(
    item.processoDetran ||
      item.ve101_num_processo ||
      item.processNumber ||
      item.processo ||
      item.processCode ||
      item.raw?.processNumber ||
      item.raw?.processo ||
      item.raw?.ve101_num_processo ||
      item.raw?.emplacar?.ve101_num_processo,
  );

const persistProcessedCombos = (mapa) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(
      PROCESSED_KEY,
      JSON.stringify(serializeProcessedCombos(mapa)),
    );
  } catch (erro) {
    // Ignora falhas na persistência
  }
};

const registerComboIfNeeded = (mapa, nfe, chassi, processoDetran) => {
  const chave = montarChave(nfe, chassi);
  if (!chave) {
    return false;
  }
  const processoNormalizado = normalizeProcessoDetranValue(processoDetran);
  const existente = mapa.get(chave);
  if (!existente) {
    mapa.set(chave, { processoDetran: processoNormalizado });
    return true;
  }
  if (processoNormalizado && processoNormalizado !== existente.processoDetran) {
    mapa.set(chave, { processoDetran: processoNormalizado });
    return true;
  }
  return false;
};

const pickFirstFilled = (...values) => {
  for (const value of values) {
    const texto = ensureString(value).trim();
    if (texto) {
      return texto;
    }
  }
  return "";
};

const extractPayloadNfe = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const requestPayload = payload.requestPayload || {};
  const veiculo = payload.veiculo || payload.vehicle || {};
  const emplacar = payload.emplacar || {};
  const raw = payload.raw || {};

  const candidato = pickFirstFilled(
    payload.nfe,
    payload.nf,
    payload.notaFiscal,
    payload.referencia,
    payload.reference,
    payload.chave,
    payload.chaveNfe,
    payload.chave_nf,
    payload.chaveNFE,
    payload.chaveNF,
    payload.ve101_key_nfe,
    payload.ve101KeyNfe,
    veiculo.ve101_key_nfe,
    emplacar.ve101_key_nfe,
    requestPayload.chaveNFE,
    requestPayload.chaveNfe,
    requestPayload.nfe,
    requestPayload.nf,
    requestPayload.chave,
    raw.nfe,
    raw.nf,
    raw.chaveNfe,
    raw.ve101_key_nfe,
  );

  return normalizarNfe(candidato);
};

const extractPayloadChassi = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const requestPayload = payload.requestPayload || {};
  const veiculo = payload.veiculo || payload.vehicle || {};
  const emplacar = payload.emplacar || {};
  const raw = payload.raw || {};

  const candidato = pickFirstFilled(
    payload.chassi,
    payload.chassis,
    payload.veiculoChassi,
    payload.vehicleChassi,
    payload.ve101_dsc_chassi,
    veiculo.chassi,
    veiculo.ve101_dsc_chassi,
    emplacar.ve101_dsc_chassi,
    requestPayload.chassi,
    requestPayload.chassis,
    requestPayload.ve101_dsc_chassi,
    raw.chassi,
    raw.chassis,
    raw.ve101_dsc_chassi,
  );

  return normalizarChassi(candidato);
};

const extractNormalizedCombo = (entry = {}) => {
  const normalizedNfe = normalizarNfe(
    entry.nfe ||
      entry.nf ||
      entry.notaFiscal ||
      entry.referencia ||
      entry.reference ||
      entry.chave ||
      entry.chaveNfe ||
      entry.chave_nf ||
      entry.ve101_key_nfe,
  );
  const normalizedChassi = normalizarChassi(
    entry.chassi ||
      entry.chassis ||
      entry.ve101_dsc_chassi ||
      entry.veiculo?.chassi ||
      entry.vehicleChassi,
  );
  return { nfe: normalizedNfe, chassi: normalizedChassi };
};

const registrarEntradaNosProcessados = (mapa, entrada, { requireProcessNumber = false } = {}) => {
  if (!entrada || typeof entrada !== "object") {
    return false;
  }
  let { nfe, chassi } = extractNormalizedCombo(entrada);
  if ((!nfe || !chassi) && entrada.raw && typeof entrada.raw === "object") {
    const rawCombo = extractNormalizedCombo(entrada.raw);
    nfe = nfe || rawCombo.nfe;
    chassi = chassi || rawCombo.chassi;
  }
  if ((!nfe || !chassi) && entrada.ignoredMetadata && typeof entrada.ignoredMetadata === "object") {
    const metaCombo = extractNormalizedCombo(entrada.ignoredMetadata);
    nfe = nfe || metaCombo.nfe;
    chassi = chassi || metaCombo.chassi;
  }
  if (!nfe || !chassi) {
    return false;
  }
  const processoDetran =
    extractProcessoDetranFromItem(entrada) ||
    extractProcessoDetranFromItem(entrada.raw) ||
    extractProcessoDetranFromItem(entrada.ignoredMetadata);
  if (requireProcessNumber && !processoDetran) {
    return false;
  }
  return registerComboIfNeeded(mapa, nfe, chassi, processoDetran);
};

const formatProcessoList = (processos = []) => {
  if (!Array.isArray(processos) || processos.length === 0) {
    return "";
  }
  const preview = processos.slice(0, 3).join(", ");
  const suffix = processos.length > 3 ? ", ..." : "";
  return ` (${preview}${suffix})`;
};

const serializeProcessedCombos = (mapa) =>
  Array.from(mapa.entries()).map(([combo, meta]) => ({
    combo,
    processoDetran: normalizeProcessoDetranValue(meta?.processoDetran),
  }));

const deserializeProcessedCombos = (dados) => {
  const mapa = new Map();
  if (Array.isArray(dados)) {
    dados.forEach((entrada) => {
      if (typeof entrada === "string") {
        mapa.set(entrada, { processoDetran: null });
        return;
      }
      if (entrada && typeof entrada === "object") {
        const combo = ensureString(entrada.combo).trim();
        if (combo) {
          mapa.set(combo, { processoDetran: normalizeProcessoDetranValue(entrada.processoDetran) });
        }
      }
    });
  }
  return mapa;
};

const identifyErrorOrigin = (payload) => {
  const tipo = ensureString(payload?.errorType);
  if (tipo) {
    return tipo.toLowerCase() === "detran" ? "Detran" : "Sistema";
  }
  const origem = ensureString(payload?.origin || payload?.origem || "");
  const mensagem = ensureString(payload?.message || payload?.mensagem || payload?.erro || "");
  if (/detra/i.test(origem) || /detra/i.test(mensagem)) {
    return "Detran";
  }
  return "Sistema";
};

const applyPayloadDetails = (item, payload) => {
  if (!payload || typeof payload !== "object") {
    return item;
  }
  const statusLower = ensureString(payload?.status).toLowerCase();
  const mensagemPadraoSucesso = SUCCESS_STATUSES.includes(statusLower)
    ? "Processo concluído com sucesso."
    : "";
  const processoDetranPayload = normalizeProcessoDetranValue(
    payload?.processoDetran ||
      payload?.processNumber ||
      payload?.processo ||
      payload?.processCode ||
      payload?.ve101_num_processo ||
      payload?.emplacar?.ve101_num_processo,
  );
  const processoDetranAtualizado =
    processoDetranPayload ||
    normalizeProcessoDetranValue(item.processoDetran) ||
    normalizeProcessoDetranValue(item.raw?.processoDetran) ||
    normalizeProcessoDetranValue(item.raw?.processNumber) ||
    null;

  return {
    ...item,
    status: payload?.status || item.status,
    mensagem:
      payload?.message ||
      payload?.mensagem ||
      mensagemPadraoSucesso ||
      item.mensagem,
    erro: payload?.error || payload?.erro || null,
    origemErro: identifyErrorOrigin(payload) || item.origemErro || null,
    requestPayload: payload?.requestPayload || payload?.payload || item.requestPayload || null,
    responseBody: payload?.responseBody || payload?.response || item.responseBody || null,
    statusCode:
      payload?.statusCode ??
      payload?.httpStatus ??
      item.statusCode ??
      null,
    processoDetran: processoDetranAtualizado,
    raw: payload || item.raw,
  };
};

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (erro) {
    return fallback;
  }
};

const LoteProgressContext = createContext(null);

const useLoteProgressContext = () => {
  const ctx = useContext(LoteProgressContext);
  if (!ctx) {
    throw new Error("useLoteProgress must be used within LoteProgressProvider");
  }
  return ctx;
};

function LoteProgressProvider({ children, onJobSuccess }) {
  const [modalLoteAberto, setModalLoteAberto] = useState(false);
  const [enviandoLote, setEnviandoLote] = useState(false);
  const [painelLoteAberto, setPainelLoteAberto] = useState(false);
  const [loteResumo, setLoteResumo] = useState(null);
  const [loteItens, setLoteItens] = useState([]);
  const [loteIgnorados, setLoteIgnorados] = useState([]);
  const processedCombosRef = useRef(new Map());
  const estadoInicializadoRef = useRef(false);

  const filtrarDuplicados = useCallback(
    (lista) => {
      const combosNoEnvio = new Set();
      const duplicados = [];
      const duplicateComboKeys = new Set();
      const validos = [];
      const resumoDuplicados = {
        repeatedInBatch: [],
        alreadyProcessed: [],
      };

      lista.forEach((item) => {
        const combo = montarChave(item.nfe, item.chassi);
        if (!combo) {
          return;
        }
        const metaProcessada = processedCombosRef.current.get(combo);
        const repetidoNoEnvio = combosNoEnvio.has(combo);
        const repetidoAnteriormente = Boolean(metaProcessada);
        const processoDetranExistente =
          item.processoDetran || metaProcessada?.processoDetran || null;

        if (repetidoNoEnvio) {
          duplicados.push(item);
          duplicateComboKeys.add(combo);
          resumoDuplicados.repeatedInBatch.push({
            combo,
            nfe: item.nfe,
            chassi: item.chassi,
          });
          return;
        }

        combosNoEnvio.add(combo);

        if (repetidoAnteriormente) {
          duplicateComboKeys.add(combo);
          resumoDuplicados.alreadyProcessed.push({
            combo,
            nfe: item.nfe,
            chassi: item.chassi,
            processoDetran: processoDetranExistente,
          });
        }

        validos.push(
          processoDetranExistente && !item.processoDetran
            ? { ...item, processoDetran: processoDetranExistente }
            : item,
        );
      });

      return {
        validos,
        duplicados,
        duplicateComboKeys,
        duplicatesResumo: resumoDuplicados,
      };
    },
    [],
  );

  const registrarProcessosExistentes = useCallback((lista) => {
    if (!Array.isArray(lista) || lista.length === 0) {
      return;
    }
    const combosAtuais = new Map(processedCombosRef.current);
    let houveAlteracao = false;
    lista.forEach((item) => {
      if (registrarEntradaNosProcessados(combosAtuais, item, { requireProcessNumber: true })) {
        houveAlteracao = true;
      }
    });
    if (houveAlteracao) {
      processedCombosRef.current = combosAtuais;
      persistProcessedCombos(combosAtuais);
    }
  }, []);

  const handleJobItem = useCallback((payload = {}) => {
    setLoteItens((prev) => {
      let encontrou = false;

      const payloadNfe = extractPayloadNfe(payload);
      const payloadChassi = extractPayloadChassi(payload);
      const payloadArquivo = normalizarArquivo(payload?.arquivo);
      const payloadArquivoComparacao = payloadArquivo.toUpperCase();
      const payloadVeiculo = ensureString(payload?.ve101_key_public).trim();
      const payloadJobItemKey = ensureString(
        payload?.jobItemKey ||
          payload?.job_item_key ||
          payload?.jobItemId ||
          payload?.job_item_id,
      ).trim();
      const payloadClientId = ensureString(
        payload?.clientId ||
          payload?.client_id ||
          payload?.clientKey ||
          payload?.clienteId ||
          payload?.cliente_id ||
          payload?.entryId ||
          payload?.entry_id,
      ).trim();
      const payloadId = ensureString(payload?.id || payload?._id).trim();
      const payloadIndex = Number.isInteger(payload?.index)
        ? payload.index
        : Number.isInteger(payload?.posicao)
        ? payload.posicao
        : Number.isInteger(payload?.position)
        ? payload.position
        : null;

      const comparar = (item) =>
        (payloadVeiculo && item.ve101_key_public === payloadVeiculo) ||
        (payloadJobItemKey && item.jobItemKey === payloadJobItemKey) ||
        (payloadNfe && item.nfe === payloadNfe) ||
        (payloadChassi && item.chassi === payloadChassi) ||
        (payloadArquivoComparacao &&
          normalizarArquivo(item.arquivo).toUpperCase() === payloadArquivoComparacao) ||
        (payloadClientId &&
          ensureString(item.clientId || item.id).trim() === payloadClientId) ||
        (payloadIndex !== null &&
          Number.isInteger(item.clientIndex) &&
          item.clientIndex === payloadIndex) ||
        (payloadId && ensureString(item.id).trim() === payloadId);

      const atualizados = prev.map((item) => {
        if (!comparar(item)) {
          return item;
        }
        encontrou = true;
        const atualizado = {
          ...item,
          nfe: payloadNfe || item.nfe,
          chassi: payloadChassi || item.chassi,
          arquivo: payloadArquivo || item.arquivo,
          jobItemKey: payloadJobItemKey || item.jobItemKey,
          ve101_key_public: payloadVeiculo || item.ve101_key_public,
          clientId: payloadClientId || item.clientId || item.id,
          clientIndex: payloadIndex !== null ? payloadIndex : item.clientIndex,
          id: payloadId || item.id,
        };
        return applyPayloadDetails(atualizado, payload);
      });

      if (!encontrou) {
        const novoItem = {
          id:
            payloadId ||
            payloadJobItemKey ||
            payloadVeiculo ||
            (payloadNfe && payloadChassi
              ? `${payloadNfe}-${payloadChassi}`
              : payloadNfe || payloadChassi) ||
            `job-${Date.now()}`,
          clientId: payloadClientId || payloadId || payloadJobItemKey || payloadVeiculo || "",
          jobItemKey: payloadJobItemKey,
          clientIndex: payloadIndex,
          ve101_key_public: payloadVeiculo,
          nfe: payloadNfe,
          chassi: payloadChassi,
          status: payload?.status || "pendente",
          mensagem: "",
          erro: null,
          arquivo: payloadArquivo,
          raw: payload,
        };
        return [...atualizados, applyPayloadDetails(novoItem, payload)];
      }

      return atualizados;
    });
  }, []);

  const {
    start: iniciarMonitoramento,
    stop: encerrarMonitoramento,
    connected: socketConectado,
    fallbackActive: fallbackAtivo,
  } = useLoteJobSubscription({
    onJobUpdate: (dados) => {
      setLoteResumo((prev) => ({
        ...(prev || {}),
        ...dados,
      }));
    },
    onJobItem: handleJobItem,
  });

  useEffect(() => {
    const statusAtual = ensureString(loteResumo?.status).toLowerCase();
    if (onJobSuccess && SUCCESS_STATUSES.includes(statusAtual)) {
      onJobSuccess();
    }
  }, [loteResumo?.status, onJobSuccess]);

  useEffect(() => {
    if (estadoInicializadoRef.current) {
      return;
    }
    if (typeof window === "undefined") {
      estadoInicializadoRef.current = true;
      return;
    }
    const combosSalvos = safeParse(window.sessionStorage.getItem(PROCESSED_KEY), []);
    processedCombosRef.current = deserializeProcessedCombos(combosSalvos);
    const armazenados = safeParse(window.sessionStorage.getItem(STORAGE_KEY), null);
    if (armazenados) {
      if (Array.isArray(armazenados.loteItens)) {
        setLoteItens(armazenados.loteItens);
      }
      if (Array.isArray(armazenados.loteIgnorados)) {
        setLoteIgnorados(armazenados.loteIgnorados);
      }
      if (armazenados.loteResumo) {
        setLoteResumo(armazenados.loteResumo);
        if (armazenados.loteResumo?.jobKey) {
          iniciarMonitoramento(armazenados.loteResumo.jobKey);
        }
      }
      if (typeof armazenados.painelLoteAberto === "boolean") {
        setPainelLoteAberto(armazenados.painelLoteAberto);
      }
    }
    estadoInicializadoRef.current = true;
  }, [iniciarMonitoramento]);

  useEffect(() => {
    if (!estadoInicializadoRef.current || typeof window === "undefined") {
      return;
    }
    const estadoParaSalvar = {
      painelLoteAberto,
      loteResumo,
      loteItens,
      loteIgnorados,
    };
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(estadoParaSalvar));
    } catch (erro) {
      // Ignora falhas na persistência
    }
  }, [painelLoteAberto, loteResumo, loteItens, loteIgnorados]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handler = () => setPainelLoteAberto(true);
    window.addEventListener("loteProgresso:open", handler);
    return () => window.removeEventListener("loteProgresso:open", handler);
  }, []);

  const progressoResumo = useMemo(
    () => calcularResumoProcessamento(loteResumo || {}, loteItens),
    [loteResumo, loteItens],
  );

  useEffect(() => {
    if (!estadoInicializadoRef.current || typeof window === "undefined") {
      return;
    }
    const combosAtuais = new Map(processedCombosRef.current);
    let houveAlteracao = false;

    loteItens.forEach((item) => {
      const statusLower = normalizeStatus(item.status);
      const devePersistir =
        isSuccessStatus(statusLower) || PROCESSED_EXTRA_STATUSES.includes(statusLower);
      if (!devePersistir) {
        return;
      }
      if (registrarEntradaNosProcessados(combosAtuais, item)) {
        houveAlteracao = true;
      }
    });

    loteIgnorados.forEach((item) => {
      if (registrarEntradaNosProcessados(combosAtuais, item, { requireProcessNumber: true })) {
        houveAlteracao = true;
      }
    });

    if (houveAlteracao) {
      processedCombosRef.current = combosAtuais;
      persistProcessedCombos(combosAtuais);
    }
  }, [loteItens, loteIgnorados]);

  const confirmarEnvioLote = useCallback(
    async (entradas) => {
      const normalizados = entradas.map((item, indice) => {
        const nf = normalizarNfe(item.nfe);
        const chassi = normalizarChassi(item.chassi);
        return {
          id: item.id,
          clientId: item.id,
          clientIndex: indice,
          arquivo: normalizarArquivo(item.nome),
          arquivoOriginal: item.nome,
          ve101_key_public: item.ve101_key_public,
          nfe: nf,
          chassi,
          status: "pendente",
          mensagem: "Aguardando atualização do processamento.",
          erro: null,
          raw: null,
        };
      });

      const completos = normalizados.filter((item) => item.nfe && item.chassi);
      const incompletos = normalizados.filter((item) => !item.nfe || !item.chassi);

      if (completos.length === 0) {
        toast.error("Informe a chave NFe e o chassi para ao menos um arquivo antes de enviar o lote.");
        return;
      }

      const {
        validos,
        duplicados,
        duplicateComboKeys,
        duplicatesResumo,
      } = filtrarDuplicados(completos);

      if (duplicateComboKeys.size > 0) {
        const repeatedCount = duplicatesResumo?.repeatedInBatch?.length || 0;
        const processedCount = duplicatesResumo?.alreadyProcessed?.length || 0;
        const mensagens = [];
        if (repeatedCount) {
          mensagens.push(
            repeatedCount === 1
              ? "1 item duplicado neste lote foi ignorado."
              : `${repeatedCount} itens duplicados neste lote foram ignorados.`,
          );
        }
        if (processedCount) {
          const processosConhecidos = (duplicatesResumo?.alreadyProcessed || [])
            .map((item) => normalizeProcessoDetranValue(item.processoDetran))
            .filter(Boolean);
          const prefixo =
            processedCount === 1
              ? "1 item já possui processo ativo."
              : `${processedCount} itens já possuem processo ativo.`;
          const sufixo = processosConhecidos.length
            ? ` Processos conhecidos${formatProcessoList(processosConhecidos)}.`
            : "";
          mensagens.push(
            `${prefixo}${sufixo} Mesmo assim enviaremos para que o backend confirme ou atualize o status.`,
          );
        }
        if (!mensagens.length) {
          const quantidade = duplicateComboKeys.size;
          mensagens.push(
            quantidade === 1
              ? "1 item duplicado foi ignorado."
              : `${quantidade} itens duplicados foram ignorados.`,
          );
        }
        toast.warning(mensagens.join(" "));
      }

      if (!validos.length) {
        return;
      }

      setEnviandoLote(true);

      try {
        encerrarMonitoramento();

        const payloadItens = validos.map((item, indice) => {
          const payloadItem = {
            nf: item.nfe,
            chassi: item.chassi,
            clientId: item.clientId,
            clientKey: item.clientId,
            index: indice,
          };
          if (item.ve101_key_public) {
            payloadItem.ve101_key_public = item.ve101_key_public;
          }
          if (item.arquivo) {
            payloadItem.arquivo = item.arquivo;
          }
          if (item.arquivoOriginal) {
            payloadItem.nomeOriginal = item.arquivoOriginal;
          }
          return payloadItem;
        });

        const resposta = await criarLoteEmplacamento(payloadItens);
        const job = resposta?.job || resposta || {};
        const jobKey = job?.jobKey || job?.key || job?.id;

        if (!jobKey) {
          throw new Error("O backend não retornou o identificador do job.");
        }

        const itensInicial = validos.map((item, indice) => ({
          id: item.id,
          clientId: item.clientId,
          clientIndex: indice,
          arquivo: item.arquivo,
          ve101_key_public: item.ve101_key_public,
          jobItemKey: "",
          jobItemIndex: indice,
          nfe: item.nfe,
          chassi: item.chassi,
          status: "pendente",
          mensagem: "Aguardando atualização do processamento.",
          erro: null,
          raw: null,
        }));

        const itensComJobInfo = associarJobItemsAosEnvios(
          itensInicial,
          job?.items || job?.itens || resposta,
        ).map((item) => {
          const { backendReferencia, ...restante } = item;
          return applyPayloadDetails(restante, backendReferencia || item.raw);
        });

        const ignoradosResposta = Array.isArray(resposta?.ignorados) ? resposta.ignorados : [];
        const ignoradosLocais = [
          ...incompletos.map((item) => ({
            id: item.id,
            motivo: INCOMPLETE_REASON,
            nfe: item.nfe,
            chassi: item.chassi,
          })),
          ...duplicados.map((item) => {
            const combo = montarChave(item.nfe, item.chassi);
            const metaProcessada = combo ? processedCombosRef.current.get(combo) : null;
            const processoDetran =
              normalizeProcessoDetranValue(item.processoDetran) ||
              metaProcessada?.processoDetran ||
              null;
            return {
              id: item.id,
              motivo: DUPLICATE_REASON,
              nfe: item.nfe,
              chassi: item.chassi,
              processoDetran,
            };
          }),
        ];

        setLoteIgnorados([...ignoradosResposta, ...ignoradosLocais]);
        setLoteItens(itensComJobInfo);
        setLoteResumo({
          ...normalizeJobUpdate(job),
          jobKey,
        });
        setPainelLoteAberto(true);
        setModalLoteAberto(false);
        toast.success("Lote enviado com sucesso.");

        iniciarMonitoramento(jobKey);
      } catch (erro) {
        const ignoradosBackend = Array.isArray(erro?.response?.data?.ignorados)
          ? erro.response.data.ignorados
          : [];
        if (ignoradosBackend.length) {
          registrarProcessosExistentes(ignoradosBackend);
        }
        const mensagemOriginal =
          erro?.response?.data?.message ||
          erro?.message ||
          "Ops! Não foi possível iniciar o processamento em lote.";
        const somenteIgnorados = /todos os itens foram ignorados/i.test(
          ensureString(mensagemOriginal),
        );
        if (somenteIgnorados) {
          const processosConhecidos = ignoradosBackend
            .map((item) => normalizeProcessoDetranValue(item.processoDetran))
            .filter(Boolean);
          const detalhe = processosConhecidos.length
            ? `Processos já existentes${formatProcessoList(processosConhecidos)}.`
            : "Os itens já possuem processo ativo e não serão reenviados.";
          toast.warning(`Nenhum item foi enviado porque todos já foram processados. ${detalhe}`);
        } else {
          toast.error(mensagemOriginal);
        }
        setPainelLoteAberto(false);
      } finally {
        setEnviandoLote(false);
      }
    },
    [encerrarMonitoramento, filtrarDuplicados, iniciarMonitoramento, registrarProcessosExistentes],
  );

  const abrirModalLote = useCallback(() => setModalLoteAberto(true), []);
  const fecharModalLote = useCallback(() => setModalLoteAberto(false), []);
  const abrirPainelLote = useCallback(() => setPainelLoteAberto(true), []);
  const fecharPainelLote = useCallback(() => setPainelLoteAberto(false), []);

  const contextValue = useMemo(
    () => ({
      abrirModalLote,
      fecharModalLote,
      abrirPainelLote,
      fecharPainelLote,
      enviandoLote,
      painelLoteAberto,
      modalLoteAberto,
      resumo: loteResumo,
      progressoPercentual: progressoResumo.percentual,
      progressoResumo,
      registrarProcessosExistentes,
    }),
    [
      abrirModalLote,
      fecharModalLote,
      abrirPainelLote,
      fecharPainelLote,
      enviandoLote,
      painelLoteAberto,
      modalLoteAberto,
      loteResumo,
      progressoResumo,
      registrarProcessosExistentes,
    ],
  );

  return (
    <LoteProgressContext.Provider value={contextValue}>
      {children}
      <LoteUploadModal
        aberto={modalLoteAberto}
        onClose={fecharModalLote}
        onConfirm={confirmarEnvioLote}
        enviando={enviandoLote}
      />
      <LoteProgressPanel
        aberto={painelLoteAberto}
        onClose={fecharPainelLote}
        resumo={loteResumo}
        itens={loteItens}
        ignorados={loteIgnorados}
        socketAtivo={socketConectado}
        usandoFallback={fallbackAtivo}
      />
    </LoteProgressContext.Provider>
  );
}

export { LoteProgressProvider, useLoteProgressContext as useLoteProgress };
